import { Label } from "flowbite-react"
import React, { ChangeEvent, useState, useRef } from "react"

import { CautionIcon } from "@assets/icons"
import useToast from "@hooks/useToast"
import UploadSvg from "@public/svg/upload.svg"

interface UploadFileProps {
  onchangeUploading: (event: ChangeEvent<HTMLInputElement>) => void
  description: JSX.Element
  className?: string
  fileSize?: string
  filesLimit?: number
  type?: number
  accept?: string
  multiple?: boolean
  onDrop?: (file: File | FileList) => void
  allowMultipleFilesDnD?: boolean
  inputId?: string
  inputRef?: any
  required?: boolean
}
interface UploadFileError {
  fileName: string
  message: string
}
const UploadFile = ({
  onchangeUploading,
  description,
  fileSize,
  filesLimit,
  className,
  multiple,
  accept,
  onDrop,
  inputId,
  inputRef,
  required,
  allowMultipleFilesDnD = false,
}: UploadFileProps) => {
  const { showToast } = useToast()
  const [errors, setErrors] = useState<UploadFileError[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const convertFileSizeToBytes = (fileSizeStr: string | undefined): number => {
    if (!fileSizeStr) return -1
    const units: Record<string, number> = { kb: 1024, mb: 1024 * 1024, gb: 1024 * 1024 * 1024 }
    // matches if input is kb | mb | gb
    const [size, unit = "b"] = fileSizeStr.match(/^(\d+)([kmgb]*)$/i)?.slice(1) ?? [
      fileSizeStr,
      "b",
    ]
    // tslint:disable-next-line:radix
    return parseInt(size) * units[unit.toLowerCase()] || parseInt(size)
  }

  const convertBytesToFileSize = (bytes: number): string => {
    const units: string[] = ["B", "KB", "MB", "GB"]
    const index: number = Math.floor(Math.log(bytes) / Math.log(1024))
    const size: number = +(bytes / 1024 ** index).toFixed(2)
    return `${size} ${units[index]}`
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors([])
    const inputFiles: FileList | any = event.target.files

    // Checks file limit
    if (filesLimit && inputFiles.length > filesLimit) {
      setErrors([
        ...errors,
        {
          fileName: "All Files",
          message: "Files limit exceeded",
        },
      ])
      return
    }
    // Removed file size chec
    // Checks file size
    // if (fileSize && inputFiles.length) {
    //   const maxFileSize = convertFileSizeToBytes(fileSize)
    //   for (const inputFile of inputFiles) {
    //     if (inputFile.size > maxFileSize) {
    //       setErrors([
    //         ...errors,
    //         {
    //           fileName: inputFile.name,
    //           message: `File too large ${convertBytesToFileSize(inputFile.size)}`
    //         }
    //       ])
    //       return
    //     }
    //   }
    // }

    // Calls the parent onchangeUploading callback function
    onchangeUploading(event)
  }

  const handleDocumentDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    // Removed file size chec
    // const maxFileSize = convertFileSizeToBytes(fileSize)
    // if (file.size > maxFileSize) {
    //   showToast({
    //     type: 'danger',
    //     heading: `${file.name} file too large ${convertBytesToFileSize(file.size)}`
    //   })
    //   return
    // }
    if (allowMultipleFilesDnD) {
      onDrop?.(e.dataTransfer.files)
    } else {
      if (file) {
        onDrop?.(file)
      }
    }
  }

  const handleDocumentDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleLabelClick = () => {
    const inputElement = inputRef?.current || fileInputRef.current
    if (inputElement) {
      inputElement.click()
    }
  }

  return (
    <>
      <div
        className={`flex w-full items-center justify-center ${className ?? ""}`}
        onDropCapture={handleDocumentDrop}
        onDragOver={handleDocumentDragOver}
      >
        <div
          className={
            "dark:hover:bg-bray-800 flex h-[124px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-gray-500 dark:hover:bg-gray-600"
          }
          onClick={handleLabelClick}
        >
          <div className={"flex flex-col items-center justify-center pt-5 pb-6"}>
            <div className={"rounded-full bg-primary-200 p-2.5"}>
              <UploadSvg />
            </div>
            {description}
          </div>
          <input
            className={"hidden"}
            id={inputId ?? "dropzone-file"}
            type={"file"}
            multiple={multiple}
            accept={accept}
            onChange={handleInputChange}
            ref={(el) => {
              fileInputRef.current = el
              if (inputRef) {
                inputRef.current = el
              }
            }}
            required={required}
          />
        </div>
      </div>
      {errors.length ? (
        <>
          {errors.map((error: UploadFileError, index) => (
            <div className={"flex w-full bg-red-50"} key={index}>
              <div
                className={
                  " flex w-full cursor-pointer flex-col  justify-center rounded-lg border-2 border-red-500"
                }
              >
                <div className={"w-full"}>
                  <div className={"flex items-center p-3 pl-4"}>
                    <div className={"rounded-md bg-red-200 p-1"}>
                      <CautionIcon className={"h-5 w-5 text-red-500"} />
                    </div>
                    <div
                      className={
                        "text20Semibold overflow-hidde text-wrap flex w-full justify-between truncate text-ellipsis px-2 text-neutral-900"
                      }
                    >
                      <div className={"test flex-grow-0"}>
                        <div className={"text12Semibold"}>
                          <Label htmlFor={"errors"} className={"text-red-600"}>
                            {error?.fileName}
                          </Label>
                        </div>
                        <div
                          className={
                            " text14Regular text12Semibold overflow-hidden truncate text-ellipsis text-red-600"
                          }
                        >
                          {error?.message}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      ) : (
        ""
      )}
    </>
  )
}

export default UploadFile
